import { FC, useRef, useEffect, useCallback } from 'react'
import { useTranslation } from 'next-i18next/pages'
import cn from 'classnames'
import s from './Modal.module.scss'
import { X as Cross } from 'react-feather'
import {
    disableBodyScroll,
    enableBodyScroll,
    clearAllBodyScrollLocks
} from 'body-scroll-lock'
import Button from '../Button'
interface Props {
    className?: string
    children?: any
    open?: boolean
    onClose: () => void
    onEnter?: () => void | null
    focusFirst?: boolean
    closeOnOverlayClick?: boolean
    allowScrolling?: boolean
}

const Modal: FC<Props> = ({
    className,
    children,
    open,
    onClose,
    onEnter = null,
    focusFirst = true,
    closeOnOverlayClick = false,
    allowScrolling = false
}) => {
    const ref = useRef() as React.MutableRefObject<HTMLDivElement>
    const { t } = useTranslation('common')
    const handleKey = useCallback(
        (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                return onClose()
            }
        },
        [onClose]
    )
    const handleOverlayClick = (event: any) => {
        if (event.target === event.currentTarget && closeOnOverlayClick) {
            onClose()
        }
        return
    }

    useEffect(() => {
        if (ref.current) {
            if (open) {
                if (!allowScrolling) disableBodyScroll(ref.current)
                window.addEventListener('keydown', handleKey)
            } else {
                if (!allowScrolling) enableBodyScroll(ref.current)
            }
        }
        return () => {
            window.removeEventListener('keydown', handleKey)
            if (!allowScrolling) clearAllBodyScrollLocks()
        }
    }, [open, handleKey])

    if (!open) {
        return null
    }

    return (
        <div className={cn(s.root, className)}>
            <div
                onKeyDown={handleOverlayClick}
                className={cn(s['modal'], 'modal-wrapper')}
                role="presentation"
                ref={ref}
                onClick={handleOverlayClick}
            >
                <div className={cn(s['modal-content'], 'modal-content')}>
                    <Button
                        variant="link"
                        onClick={() => onClose()}
                        aria-label={t('Close panel')}
                        className={cn(s['close-panel'], 'close-panel')}
                    >
                        <Cross />
                    </Button>
                    <div className={cn(s['modal-body'], 'modal-body')}>
                        {children}
                    </div>
                </div>
            </div>
        </div>
    )
}

export default Modal
